import { useState } from 'react';
import { Loader2, Users } from 'lucide-react';
import { IMPORTANT_CHAIN_TOO_SHORT, MIN_IMPORTANT_ASSIGNEES } from '@pms/shared';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AssigneePicker } from './AssigneePicker';
import { fullName } from './taskConstants';

interface Assignee {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
}

interface ManageAssigneesModalProps {
  onClose: () => void;
  onSave: (add: string[], remove: string[]) => void;
  busy?: boolean;
  current: Assignee[];
  /** Important tasks are a fallback chain and need at least MIN_IMPORTANT_ASSIGNEES. */
  isImportant?: boolean;
}

/**
 * One list, not two: people already on the task come in ticked, so unticking is
 * how you take someone off. Changes are batched and applied on Save — an untick
 * shouldn't fire a delete the instant it's clicked.
 * Mounted only while open (the caller renders it conditionally), so every opening
 * re-seeds from `current` without a reset effect.
 */
export function ManageAssigneesModal({
  onClose, onSave, busy = false, current, isImportant = false,
}: ManageAssigneesModalProps) {
  const currentIds = current.map((a) => a.user_id);
  const [selected, setSelected] = useState<string[]>(currentIds);

  const add = selected.filter((id) => !currentIds.includes(id));
  const remove = current.filter((a) => !selected.includes(a.user_id));
  const dirty = add.length > 0 || remove.length > 0;

  // Blocked here as well as on the server, and not only for convenience: the caller
  // applies removals one at a time in a loop, so a server rejection part-way through
  // would leave the earlier ones already committed.
  const tooShort = isImportant && selected.length < MIN_IMPORTANT_ASSIGNEES;
  // A task with nobody on it is invisible to everyone, so the last person can't be unticked.
  const noAssignee = selected.length === 0;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md gap-3">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" /> Manage Assignees
          </DialogTitle>
          <DialogDescription>
            Tick to put someone on this task, untick to take them off.
          </DialogDescription>
        </DialogHeader>

        <AssigneePicker selected={selected} onChange={setSelected} />

        {/* Removals are named — unticking should never be silent. */}
        {dirty && (
          <div className="rounded-md border border-border/60 bg-muted/40 px-2.5 py-1.5 text-[11px]">
            {add.length > 0 && (
              <p className="text-emerald-700 dark:text-emerald-400">
                Adding {add.length}
              </p>
            )}
            {remove.length > 0 && (
              <p className="text-rose-600 dark:text-rose-400">
                Removing: {remove.map((a) => fullName(a.first_name, a.last_name)).join(', ')}
              </p>
            )}
          </div>
        )}

        {noAssignee && (
          <p className="rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[11px] font-medium text-rose-600 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-400">
            A task needs at least one assignee.
          </p>
        )}

        {tooShort && !noAssignee && (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] font-medium text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400">
            {IMPORTANT_CHAIN_TOO_SHORT}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={busy || !dirty || tooShort || noAssignee}
            onClick={() => onSave(add, remove.map((a) => a.user_id))}
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
